/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../config/api';
import '../styles/HomePage.css';
import '../styles/FeaturedInternships.css';

const FeaturedInternships = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchInternships = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await axios.get(buildApiUrl('/api/public/featured-internships'));
        const payload = response.data;

        if (payload?.success && Array.isArray(payload.data)) {
          setInternships(payload.data);
        } else if (Array.isArray(payload)) {
          setInternships(payload);
        } else {
          console.warn('⚠️ Featured internships payload missing data array', payload);
          setInternships([]);
        }
      } catch (err) {
        console.error('❌ Error fetching featured internships:', err);
        setError('We could not load internships right now. Please try again in a few minutes.');
        setInternships([]);
      } finally {
        setLoading(false);
      }
    };

    fetchInternships();
  }, []);

  const handleNavigateToInternship = (internshipId, applyUrl) => {
    if (applyUrl) {
      window.open(applyUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!internshipId) {
      alert('Internship details are temporarily unavailable. Please try another listing.');
      return;
    }

    // Navigate to job details - unauthenticated users will see sign-up prompt if job can't be loaded
    navigate(`/job-details/${internshipId}`);
  };

  const renderPostedText = (postedAt, index) => {
    if (!postedAt) {
      return index < 3 ? 'Featured this week' : 'Recently added';
    }

    const date = new Date(postedAt);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.max(1, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

    if (diffDays === 1) return 'Posted yesterday';
    if (diffDays < 7) return `Posted ${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    if (diffDays < 30) {
      const weeks = Math.floor(diffDays / 7);
      return `Posted ${weeks} week${weeks > 1 ? 's' : ''} ago`;
    }
    const months = Math.floor(diffDays / 30);
    return `Posted ${months} month${months > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="featured-internships-page">
      <div className="featured-internships-hero">
        <h1>Featured Internships</h1>
        <p>
          Discover curated internship opportunities from verified employers and communities on AksharJobs.
          Each role is selected to help you gain experience, grow your network, and launch your career.
        </p>
        <div className="hero-actions">
          <Link to="/" className="hero-action-btn outline">
            ⟵ Back to Home
          </Link>
          <Link to="/signup" className="hero-action-btn solid">
            Create Free Profile
          </Link>
        </div>
      </div>

      <div className="featured-internships-content">
        <section className="featured-jobs">
          <div className="jobs-container">
            <h2 className="section-title">Opportunities waiting for you</h2>
            <p className="section-subtitle">
              Apply to internships across technology, business, design, marketing, and more.
            </p>

            {loading ? (
              <div className="loading-jobs">
                <div className="spinner"></div>
                <p>Loading featured internships...</p>
              </div>
            ) : error ? (
              <div className="no-jobs">
                <p>{error}</p>
                <button className="hero-action-btn solid retry" onClick={() => window.location.reload()}>
                  Retry
                </button>
              </div>
            ) : internships.length === 0 ? (
              <div className="no-jobs">
                <p>No internships are available right now. Please check back soon!</p>
              </div>
            ) : (
              <div className="featured-jobs-grid">
                {internships.map((internship, index) => {
                  const skills = Array.isArray(internship.skills)
                    ? internship.skills
                    : Array.isArray(internship.required_skills)
                      ? internship.required_skills
                      : [];

                  const location =
                    internship.location ||
                    [internship.city, internship.state, internship.country].filter(Boolean).join(', ') ||
                    internship.remoteType ||
                    'Flexible';

                  const stipend =
                    internship.salaryDisplay ||
                    internship.stipend ||
                    internship.stipendAmount ||
                    'Stipend details shared during selection';

                  const duration =
                    internship.duration ||
                    internship.experienceLevel ||
                    internship.eligibility ||
                    'Internship Opportunity';

                  const internshipId = internship.id || internship._id;
                  const isFeatured = internship.isFeatured ?? index < 6;

                  return (
                    <div className="job-card" key={internshipId || index}>
                      {isFeatured && <div className="featured-badge">Featured</div>}
                      <h3 className="job-title">{internship.title || 'Internship Opportunity'}</h3>
                      <p className="company-name">
                        {internship.company || internship.companyName || internship.company_name || 'Company'}
                      </p>

                      <div className="job-details">
                        <div className="job-detail">
                          <span className="icon">📍</span>
                          <span>{location}</span>
                        </div>
                        <div className="job-detail">
                          <span className="icon">💼</span>
                          <span>Internship</span>
                        </div>
                        <div className="job-detail">
                          <span className="icon">⏳</span>
                          <span>{duration}</span>
                        </div>
                        <div className="job-detail salary">
                          <span className="icon">💰</span>
                          <span>{stipend}</span>
                        </div>
                      </div>

                      <div className="skills">
                        {skills.slice(0, 3).map((skill, idx) => (
                          <span key={idx} className="skill-tag">{skill}</span>
                        ))}
                        {skills.length > 3 && (
                          <span className="skill-tag">+{skills.length - 3} more</span>
                        )}
                      </div>

                      <div className="job-card-footer">
                        <div className="job-actions">
                          <button
                            className="btn-apply"
                            onClick={() => handleNavigateToInternship(internshipId, internship.applyUrl)}
                          >
                            <span>🚀</span>
                            Apply Now
                          </button>
                          <button
                            className="btn-view"
                            onClick={() => handleNavigateToInternship(internshipId, null)}
                          >
                            <span>👀</span>
                            View Details
                          </button>
                        </div>
                        <div className="posted-date">
                          <span>🕒</span>
                          <span>{renderPostedText(internship.postedAt, index)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default FeaturedInternships;


